import { mapOktaLanguageCode } from '@shure/cloud/shared/services/organizations-store-service';

import { footerUrls } from './footer-urls';
import { RegionContactInfo } from './footer.model';

type PageKeys = keyof typeof footerUrls;

export const urlLanguageCodes: Map<string, string> = new Map([
	['en', 'en-US'],
	['de', 'de-DE'],
	['fr', 'fr-FR'],
	['es', 'es-LATAM'],
	['pt', 'pt-BR'],
	['ja', 'ja-JP'],
	['ko', 'en-US'],
	['zh-cn', 'zh-CN']
]);
export const regionCodesByLanguage: Map<string, string> = new Map([
	['en', 'en-US'],
	['de', 'de-DE'],
	['fr', 'fr-CH'],
	['es', 'es'],
	['pt', 'pt-BR'],
	['ja', 'ja'],
	['ko', 'en-US'],
	['zh-cn', 'zh-CN']
]);

export const specialRegionMap: Map<string, string> = new Map<string, string>([
	['United Kingdom', 'en-GB'],
	['United States', 'en-US'],
	['Switzerland', 'de-DE'],
	['Netherlands', 'nl'],
	['Hong Kong S.A.R.', 'HK'],
	['China', 'zh-CN'],
	['Japan', 'ja-JP'],
	['Germany', 'de-DE']
]);

/**
 * Gets the contact number and country information based on the provided region name.
 * If the region is not matched, a default "Global" fallback is returned.
 * @param region - Region name (e.g., 'Germany', 'Japan', etc.)
 * @returns RegionContactInfo containing country and contact number.
 */
export function getContactInfoByRegion(region: string): RegionContactInfo {
	const regionMap = new Map<string, RegionContactInfo>([
		['Germany', { country: 'Germany', contactNumber: '+49 7262 9974000' }],
		['China', { country: 'China', contactNumber: '+86 21 6137 5019' }],
		['United Kingdom', { country: 'United Kingdom', contactNumber: '+44 1992 703020' }],
		['Switzerland', { country: 'Switzerland', contactNumber: '+41 44 505 66 64' }],
		['Netherlands', { country: 'Netherlands', contactNumber: '+31 345 799 339' }],
		['Japan', { country: 'Japan', contactNumber: '+81 50 1721 8109' }],
		['Hong Kong S.A.R.', { country: 'Hong Kong S.A.R.', contactNumber: '+852 2553 3907' }],
		['United States', { country: 'United States', contactNumber: '+1 847 600 6400' }],
		['default', { country: 'Global', contactNumber: '' }]
	]);

	const regionKey = region;
	const contactInfo = regionMap.get(regionKey);
	return contactInfo ?? regionMap.get('default')!;
}

/**
 * Constructs the "Contact Us" URL for a given page, using region and language settings.
 *
 * @param pageKey - Identifier for the page whose Contact Us link should be generated.
 * @param region - Optional region name (e.g., "India", "US").
 * @param langCode - Optional language code (e.g., "en_US", "zh_CN").
 * @returns A localized Contact Us URL.
 */
export function getContactUsUrl(pageKey: PageKeys, region?: string, langCode?: string): string {
	const urlTemplate = footerUrls[pageKey] || '';
	const languageCode = langCode || 'en_US';
	const mappedLangCode = mapOktaLanguageCode[languageCode] || 'en';
	const regionInput = region || '';

	// Resolve the region code based on input; fall back to language-based region code or 'en-US'
	const regionCode = specialRegionMap.get(regionInput) || 'en-US';
	// Define which full language codes are explicitly supported
	const supportedLangs = new Set(['pt_BR', 'ko_KR', 'en_US', 'zh_CN', 'nl_NL']);
	// Use full language code if supported, otherwise fallback to simplified code
	const resolvedLanguageCode = supportedLangs.has(languageCode) ? languageCode : mappedLangCode;
	// Replace placeholders in the URL template with actual language and region codes
	return urlTemplate.replace('{LANGUAGE_CODE}', resolvedLanguageCode).replace('{REGION_CODE}', regionCode);
}

/**
 *	formatting footer url's based on the user's locale
 * @param locale - string representation of user's selected locale
 */
export function getFormattedUrl(pageKey: PageKeys): string {
	const storedLang = localStorage.getItem('locale') || 'en';
	const localeCode = urlLanguageCodes.get(storedLang) || 'en-US';
	let url = footerUrls[pageKey] || '';

	if (pageKey === 'CONTACT_US') {
		const regionCode = regionCodesByLanguage.get(storedLang) || 'en-US';
		url = url.replace('{REGION_CODE}', regionCode);
		if (storedLang === 'zh-cn') {
			return url.replace('{LANGUAGE_CODE}', 'zh_CN');
		}
		const replaceCode =
			storedLang === 'pt' ? 'pt_BR' : storedLang === 'ko' || storedLang === 'en' ? 'en_US' : storedLang;
		return url.replace('{LANGUAGE_CODE}', replaceCode);
	}

	if (pageKey === 'PRIVACY_POLICY') {
		const privacyMap: Record<string, string> = {
			es: 'privacidad',
			de: 'datenschutz',
			pt: 'privacidade',
			fr: 'confidentialite'
		};

		const replacement = privacyMap[storedLang];
		if (replacement) {
			url = url.replace('privacy', replacement);
		}
	}
	return url.replace('{LANGUAGE_CODE}', localeCode);
}
